/**
 * Pronoun antecedents the caller names outright (D235) — one ref per pronoun
 * WORD, ahead of recency.
 *
 * @remarks
 * Recency answers "what was the last thing spoken of"; it cannot answer "who
 * am I talking to". The engine knows the second — the body the player last
 * addressed, or that last asked the player a question — and hands it here as
 * the antecedent of that body's own pronoun (`her` for a `she`). The parser
 * applies no world knowledge of its own: it takes the map as given and uses
 * it exactly where the clause's pronoun names it.
 *
 * The view is reshaped per CLAUSE, because the resolution rungs read one
 * most-recent-first list and take its head for any pronoun: a clause whose
 * only pronoun word has a named antecedent gets that ref at the head of its
 * view; a clause with two different pronouns (`ask her about it`) keeps the
 * recency view unchanged, since one head cannot serve two words — the reading
 * such a clause has always had.
 *
 * @packageDocumentation
 */

import { type CanonicalToken } from '../grammar/canonicalize-tokens.js';
import { type PronounWord } from '../lexicon/builtin-function-words.js';
import { type EntityRef } from '../types.js';

/** What each pronoun word means to the caller right now, where the caller knows (the engine's conversational partner, D235). */
export type PronounAntecedents = Partial<Readonly<Record<PronounWord, EntityRef>>>;

/**
 * The recency view one clause resolves its pronouns against, with the
 * caller's antecedent for the clause's one pronoun word at its head.
 *
 * @param canonicalTokens - The clause's canonical tokens
 * @param recencyView - The most-recent-first view the clause would otherwise read
 * @param antecedents - The caller's named antecedents, if any
 * @returns The view for this clause — the recency view itself unless exactly one pronoun word appears and the caller names it
 */
export const recencyViewForClause = (
	canonicalTokens: readonly CanonicalToken[],
	recencyView: readonly EntityRef[],
	antecedents: PronounAntecedents | undefined,
): readonly EntityRef[] => {
	if (antecedents === undefined) {
		return recencyView;
	}

	const pronounWords = new Set(canonicalTokens.flatMap((token) => (token.kind === 'pronoun' ? [token.pronoun] : [])));

	if (pronounWords.size !== 1) {
		return recencyView;
	}

	// Exactly one word, by the check above.
	const antecedent = antecedents[[...pronounWords][0] as PronounWord];

	if (antecedent === undefined) {
		return recencyView;
	}

	return [antecedent, ...recencyView.filter((entityRef) => entityRef !== antecedent)];
};
